import React from 'react';
import TextBox from 'components/TextBox';
import i18n from 'constants/translation/i18n';
import CustomIconButton from 'components/CustomIconButton';
import CustomSelect from 'components/CustomSelect';
import TextAreaBoxSolutions from 'components/TextAreaBoxSolutions';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { IGapsInfo, IAudits } from 'store/ducks/Gaps/types';
import { solutionsOp } from 'store/ducks/Solutions';
import { YesNo } from 'store/ducks/globalTypes';
import Helpers from 'utils/Helpers';
import { PlusOutlined } from '@ant-design/icons';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { ISolutionTableRow } from 'store/ducks/Solutions/types';
import * as Styled from 'pages/SolutionDefinition/SolutionDefinition.styled';

const SolutionColumns = ({
  solutionTypes,
  approversList,
  permitted,
  dispatch,
  handleStatusChange,
  handlePlusClick,
  showSearch
}: {
  solutionTypes: string[];
  approversList: string[];
  permitted: boolean;
  dispatch: any;
  handleStatusChange: (value: YesNo, solId: string | number) => void;
  handlePlusClick: (row: ISolutionTableRow) => void;
  showSearch: boolean;
}) => {
  const solutionColumns = [
    {
      title: i18n.t('CATEGORY'),
      width: 100,
      render: (_: any, row: ISolutionTableRow) => ({
        children: (
          <CustomSelect options={[]} defaultValue={row.category} disabled />
        ),
        props: {
          rowSpan: row.bruRowSpan
        }
      })
    },

    {
      title: i18n.t('CLN_REQ'),
      width: showSearch ? 700 : 500,
      render: (_: any, row: ISolutionTableRow) => ({
        children: (
          <TextBox width={showSearch ? 700 : 500} level={row.level}>
            {row.value}
          </TextBox>
        ),
        props: {
          rowSpan: row.bruRowSpan
        }
      })
    },

    {
      title: i18n.t('GAPS'),
      width: 335,
      render: (_: any, row: ISolutionTableRow) =>
        !Helpers.isObjEmpty(row.GAPS) ? (
          <Styled.TextBoxContainer>
            {Object.values(row.GAPS).map((gap: IGapsInfo) => (
              <TextBox key={gap.gap_id} width={335}>
                {gap.gap_desc}
              </TextBox>
            ))}
          </Styled.TextBoxContainer>
        ) : (
          <Styled.StyledTextBox width={335} />
        )
    },

    {
      title: i18n.t('AUDIT_FINDINGS'),
      width: 335,
      render: (_: any, row: ISolutionTableRow) =>
        !Helpers.isObjEmpty(row.AUDITS) ? (
          <Styled.TextBoxContainer>
            {Object.values(row.AUDITS).map((audit: IAudits) => (
              <TextBox key={generateUniqueId()} width={335}>
                {audit.desc}
              </TextBox>
            ))}
          </Styled.TextBoxContainer>
        ) : (
          <Styled.StyledTextBox width={335} />
        )
    },

    {
      title: i18n.t('SOLUTIONS'),
      width: 376,
      render: (_: any, row: ISolutionTableRow) => (
        <>
          {!Helpers.isObjEmpty(row.SOLUTIONS) && (
            <TextAreaBoxSolutions
              solutions={row.SOLUTIONS}
              solId={row.sol_id}
            />
          )}

          {row.isLastSolutionRow && (
            <Styled.ButtonContainer>
              <CustomIconButton
                type='primary'
                shape='circle'
                icon={<PlusOutlined />}
                size='middle'
                onClick={() => handlePlusClick(row)}
              />
            </Styled.ButtonContainer>
          )}
        </>
      )
    },

    {
      title: i18n.t('SOLUTION_TYPE'),
      width: 100,
      render: (_: any, row: ISolutionTableRow) => {
        const options = Helpers.convertToSelectOptions(solutionTypes);

        return (
          <CustomSelect
            options={options}
            defaultValue={row.SOLUTIONS?.sol_type}
            onChange={(value: string) => {
              dispatch(
                solutionsOp.editSolutionValue(
                  row.sol_id,
                  row.SOLUTIONS,
                  'sol_type',
                  value
                )
              );

              dispatch(complianceEstOp.setSaveDisabled(false));
            }}
          />
        );
      }
    },

    {
      title: i18n.t('EVIDENCE_REQUIRED'),
      width: 150,
      render: (_: any, row: ISolutionTableRow) => (
        <CustomSelect
          size='lg'
          options={[
            {
              value: YesNo.YES,
              label: 'Required'
            },
            {
              value: YesNo.NO,
              label: 'Not required'
            }
          ]}
          defaultValue={row.SOLUTIONS?.evd_required}
          onChange={(value: string) =>
            dispatch(
              solutionsOp.editSolutionValue(
                row.sol_id,
                row.SOLUTIONS,
                'evd_required',
                value
              )
            )
          }
        />
      )
    },

    {
      title: i18n.t('APPROVER'),
      width: 150,
      render: (_: any, row: ISolutionTableRow) => (
        <CustomSelect
          size='lg'
          options={Helpers.convertToSelectOptions(approversList)}
          defaultValue={row.SOLUTIONS?.approver}
          onChange={(value: string) =>
            dispatch(
              solutionsOp.editSolutionValue(
                row.sol_id,
                row.SOLUTIONS,
                'approver',
                value
              )
            )
          }
        />
      )
    },

    {
      title: 'Solution status',
      width: 150,
      render: (_: any, row: ISolutionTableRow) => {
        const isNewSolution = row.isNew === true;

        const isDisabled =
          isNewSolution || !permitted || !row.SOLUTIONS?.sol_approved;

        return (
          <CustomSelect
            size='lg'
            options={[
              {
                label: 'Approved',
                value: YesNo.YES
              },
              {
                label: 'Not approved',
                value: YesNo.NO
              }
            ]}
            defaultValue={isDisabled ? '' : row.SOLUTIONS?.sol_approved}
            disabled={isDisabled}
            onChange={(value: YesNo) => handleStatusChange(value, row.sol_id)}
          />
        );
      }
    }
  ];

  return solutionColumns;
};

const TaskColumns = () => {
  const taskColumns = [
    {
      title: i18n.t('SOLUTIONS'),
      width: 500,
      render: () => <TextBox width={500} />
    },
    {
      title: i18n.t('TASK_DEFINITION'),
      width: 366,
      render: () => <TextBox width={366} />
    },
    {
      title: i18n.t('TASK_TYPE'),
      width: 100,
      render: () => <TextBox width={100} />
    },
    {
      title: i18n.t('TASK_STATUS'),
      width: 120,
      render: () => <TextBox width={120} />
    },
    {
      title: i18n.t('OWNER'),
      width: 150,
      render: () => <TextBox width={150} />
    },
    {
      title: i18n.t('DUE_DATE'),
      width: 128,
      render: () => <TextBox width={128} />
    }
  ];
  return taskColumns;
};

const AuditFindingColumns = () => {
  const auditFindingColumns = [
    {
      title: i18n.t('CLN_REQ'),
      width: 300,
      render: () => <TextBox width={300} />
    },
    {
      title: i18n.t('CATEGORY'),
      width: 100,
      render: () => <TextBox width={100} />
    },
    {
      title: i18n.t('ALIGNED_AUDIT_FINDINGS'),
      width: 676,
      render: () => <TextBox width={676} />
    }
  ];
  return auditFindingColumns;
};

const EvidenceDefinitionColumns = () => {
  const evidenceDefinitionColumns = [
    {
      title: i18n.t('CLN_REQ'),
      width: 500,
      render: () => <TextBox width={500} />
    },
    {
      title: i18n.t('SOLUTION_DEFINITION'),
      width: 500,
      render: () => <TextBox width={500} />
    },
    {
      title: i18n.t('SOLUTION_TYPE'),
      width: 100,
      render: () => <TextBox width={100} />
    },
    {
      title: i18n.t('EVIDENCE_DEFINITION'),
      width: 376,
      render: () => <TextBox width={376} />
    },
    {
      title: i18n.t('EVIDENCE_FILE'),
      width: 350,
      render: () => <TextBox width={350} />
    },
    {
      title: i18n.t('APPROVER'),
      width: 150,
      render: () => <TextBox width={150} />
    },
    {
      title: i18n.t('APPROVAL'),
      width: 150,
      render: () => <TextBox width={150} />
    }
  ];
  return evidenceDefinitionColumns;
};

const GapAdditionalInfoColumns = () => {
  const gapAdditionalInfoColumns = [
    {
      title: i18n.t('GAPS'),
      width: 335,
      render: () => <TextBox width={335} />
    },
    {
      title: i18n.t('GAP_DUE_DATE'),
      width: 300,
      render: () => <TextBox width={300} />
    },
    {
      title: i18n.t('DUE_DATE_AUDIT'),
      width: 195,
      render: () => <TextBox width={195} />
    },
    {
      title: i18n.t('GAP_PRIORITIZATION'),
      width: 120,
      render: () => <TextBox width={120} />
    },
    {
      title: i18n.t('AUDIT_PRIORITIZATION'),
      width: 195,
      render: () => <TextBox width={195} />
    },
    {
      title: i18n.t('GAP_SOURCE'),
      width: 120,
      render: () => <TextBox width={120} />
    },
    {
      title: i18n.t('SOURCE_AUDIT'),
      width: 195,
      render: () => <TextBox width={195} />
    },
    {
      title: i18n.t('OWNER_NOTIFIED'),
      width: 100,
      render: () => <TextBox width={100} />
    },
    {
      title: i18n.t('GAP_OWNER'),
      width: 150,
      render: () => <TextBox width={150} />
    },
    {
      title: i18n.t('AUDIT_OWNER'),
      width: 195,
      render: () => <TextBox width={195} />
    }
  ];
  return gapAdditionalInfoColumns;
};

export {
  SolutionColumns,
  TaskColumns,
  AuditFindingColumns,
  GapAdditionalInfoColumns,
  EvidenceDefinitionColumns
};
